import { creativeLibraryApi } from "@/lib/api/creative-library";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "@digital-twins/design-system/icons";
import type { CreativeSubject } from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { CreativeFilingFields } from "@/components/creative-materials/CreativeFilingFields";
import type { CharacterAssignmentDialogProps } from "@/types/creative-library";
import { CharacterChoice } from "./CharacterChoice";
import { CreativeEditorDialog } from "./CreativeEditorDialog";
import { CreativeModelFields } from "./CreativeModelFields";
import { PrivateImage } from "./PrivateImage";
import { attachSubjectReferences } from "./attachSubjectReferences";
import { useBrandSubjects } from "./useBrandSubjects";
import { useModelDraft } from "./useModelDraft";

export function CharacterAssignmentDialog({
  item,
  brands,
  onClose,
  onSaved,
  onOpenSubject,
}: CharacterAssignmentDialogProps) {
  const { t } = useTranslation("app");
  const [filedBrand, setFiledBrand] = useState(item.brandToken);
  const [brandToken, setBrandToken] = useState(item.brandToken ?? "none");
  const [subjectToken, setSubjectToken] = useState("none");
  const [createdToken, setCreatedToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<"create" | "attach" | null>(null);
  const [attached, setAttached] = useState<CreativeSubject | null>(null);
  const isSubmitting = useRef(false);
  const brand = brands.find((value) => value.token === brandToken);
  const list = useBrandSubjects(brand?.token);
  const draft = useModelDraft(brandToken, list);
  const canEdit = Boolean(brand?.canEdit && item.canEdit);
  const hasNoModels = list.ready && list.subjects.length === 0;
  const isNewModel = canEdit && list.ready && (hasNoModels || draft.chosen);
  const canSubmit =
    brandToken !== "none" &&
    canEdit &&
    (isNewModel ? Boolean(draft.name.trim()) : subjectToken !== "none");

  async function save() {
    if (isSubmitting.current || !canSubmit) {
      return;
    }
    isSubmitting.current = true;
    setSaving(true);
    setError(null);
    let target = subjectToken;
    if (isNewModel) {
      try {
        const created = await draft.create();
        setSubjectToken(created.token);
        setCreatedToken(created.token);
        onSaved();
        target = created.token;
      } catch {
        setError("create");
        setSaving(false);
        isSubmitting.current = false;
        return;
      }
    }
    try {
      if (!filedBrand) {
        await creativeLibraryApi.updateCreative(item.token, {
          revision: item.revision,
          brandToken,
          productToken: null,
        });
        setFiledBrand(brandToken);
        onSaved();
      }
      setAttached(await attachSubjectReferences(target, [item.token]));
      onSaved();
    } catch {
      setError("attach");
    } finally {
      setSaving(false);
      isSubmitting.current = false;
    }
  }

  const modelFields = (
    <CreativeModelFields
      name={draft.name}
      kind={draft.kind}
      disabled={saving}
      onNameChange={draft.setName}
      onKindChange={draft.setKind}
      onSubmit={() => void save()}
    />
  );
  return (
    <CreativeEditorDialog
      open
      busy={saving}
      onClose={onClose}
      kind="subjects"
      title={t("creativeLibrary.addToCharacter")}
      description={item.title}
      testId="character-assignment-dialog"
      footer={
        attached ? (
          <>
            <Button variant="outline" onClick={onClose}>
              {t("creativeLibrary.done")}
            </Button>
            {onOpenSubject && (
              <Button
                variant="primary"
                onClick={() => {
                  onClose();
                  onOpenSubject(attached);
                }}
                data-testid="assignment-open-character"
              >
                {t("creativeLibrary.openCharacter")}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button variant="outline" disabled={saving} onClick={onClose}>
              {t("creativeLibrary.cancel")}
            </Button>
            {(canEdit || !hasNoModels) && (
              <Button
                variant="primary"
                disabled={saving || !canSubmit}
                onClick={() => void save()}
                data-testid="assignment-save"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {t(
                  saving
                    ? "creativeLibrary.adding"
                    : isNewModel
                      ? "creativeLibrary.createAndAdd"
                      : "creativeLibrary.addToCharacter",
                )}
              </Button>
            )}
          </>
        )
      }
    >
      {attached ? (
        <Text role="status">
          {t("creativeLibrary.addedToCharacter", { name: attached.name })}
        </Text>
      ) : (
        <>
          <div className="flex items-start gap-4">
            <PrivateImage
              source={item.mediaUrl}
              alt={item.title}
              className="h-24 w-24 shrink-0 rounded-lg border border-border bg-muted object-contain"
              testId="character-assignment-image"
            />
            <div className="min-w-0 flex-1">
              <CreativeFilingFields
                brands={brands}
                products={[]}
                brandToken={brandToken}
                productToken="none"
                hideProduct
                disabled={saving || Boolean(filedBrand) || Boolean(createdToken)}
                testIdPrefix="character-assignment"
                onChange={(next) => {
                  setBrandToken(next);
                  setSubjectToken("none");
                  setError(null);
                  draft.clear();
                }}
              />
            </div>
          </div>
          {brandToken === "none" ? (
            <Text size="sm" color="muted">
              {t("creativeLibrary.assignCharacterBrandFirst")}
            </Text>
          ) : hasNoModels ? (
            canEdit ? (
              modelFields
            ) : (
              <Text size="sm" color="muted">
                {t("creativeLibrary.noModelsNoRights")}
              </Text>
            )
          ) : (
            <>
              <CharacterChoice
                brand={brand}
                value={subjectToken}
                disabled={saving}
                list={list}
                creatingNew={isNewModel}
                onChange={(token) => {
                  setSubjectToken(token);
                  setError(null);
                  draft.clear();
                }}
                onCreate={() => {
                  setSubjectToken("none");
                  setError(null);
                  draft.choose();
                }}
              />
              {isNewModel && modelFields}
            </>
          )}
          {error && (
            <Text role="alert" className="text-destructive">
              {t(
                error === "create"
                  ? "creativeLibrary.modelCreateFailed"
                  : "creativeLibrary.characterAssignmentFailed",
              )}
            </Text>
          )}
        </>
      )}
    </CreativeEditorDialog>
  );
}
